import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { itemEnvelope, zAddress, zId, zInstant, zMinor } from './envelope';
import { apiRequest } from './http';
import { applyWrite } from './invalidate';
import { useIdempotentMutation } from './mutations';
import { defineResource, useResourceItem, useResourceList, type ListParams } from './resource';

export const TAX_ID_TYPES = [
  { value: 'ca_bn', label: 'CA business number' },
  { value: 'ca_gst_hst', label: 'CA GST/HST' },
  { value: 'ca_qst', label: 'CA QST' },
  { value: 'ca_pst_bc', label: 'CA PST (BC)' },
  { value: 'ca_pst_mb', label: 'CA PST (MB)' },
  { value: 'ca_pst_sk', label: 'CA PST (SK)' },
  { value: 'us_ein', label: 'US EIN' },
  { value: 'eu_vat', label: 'EU VAT' },
  { value: 'gb_vat', label: 'GB VAT' },
  { value: 'other', label: 'Other' },
] as const;

/** BillerAPI `CustomerResult`. */
export const customerSchema = z.object({
  id: zId,
  name: z.string(),
  legal_name: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  billing_emails: z.array(z.string()),
  currency: z.string().nullable(),
  locale: z.string().nullable(),
  time_zone: z.string().nullable(),
  billing_address: zAddress.nullable(),
  tax_ids: z.array(z.object({ type: z.string(), value: z.string() })),
  tax_exempt: z.boolean(),
  tax_rate_ids: z.array(z.string()),
  payment_terms_days: z.number().int(),
  invoice_delivery: z.string(),
  autopay_enabled: z.boolean(),
  default_payment_method_id: z.string().nullable(),
  collection_policy_id: z.string().nullable(),
  parent_customer_id: z.string().nullable(),
  bill_to_parent: z.boolean(),
  partner_id: z.string().nullable().default(null),
  invoice_grouping: z.enum(['subscription', 'consolidated']),
  legal_entity_id: z.string().nullable().default(null),
  external_reference: z.string().nullable(),
  metadata: z.record(z.string(), z.string().nullable()),
  status: z.enum(['active', 'archived']),
  archived_at: zInstant.nullable(),
  version: z.number().int(),
  created_at: zInstant.nullable(),
  updated_at: zInstant.nullable(),
});
export type Customer = z.infer<typeof customerSchema>;

export const customers = defineResource('customers', '/customers', customerSchema);

export interface CustomerListParams extends ListParams {
  search?: string | undefined;
  status?: 'active' | 'archived' | undefined;
  external_reference?: string | undefined;
  parent_customer_id?: string | undefined;
  partner_id?: string | undefined;
}

export function useCustomers(params: CustomerListParams = {}, options: { enabled?: boolean } = {}) {
  return useResourceList(customers, params, options);
}

export function useCustomer(id: string | undefined) {
  return useResourceItem(customers, id);
}

/** Writable attributes (POST requires `name`; PATCH sends only what changed). */
export interface CustomerInput {
  name?: string;
  legal_name?: string | null;
  email?: string | null;
  phone?: string | null;
  billing_emails?: string[];
  currency?: string | null;
  locale?: string;
  time_zone?: string | null;
  billing_address?: {
    line1?: string | null;
    line2?: string | null;
    city?: string | null;
    region?: string | null;
    postal_code?: string | null;
    country?: string | null;
  } | null;
  tax_ids?: { type: string; value: string }[];
  tax_exempt?: boolean;
  tax_rate_ids?: string[];
  payment_terms_days?: number;
  invoice_delivery?: 'email' | 'portal' | 'none';
  autopay_enabled?: boolean;
  parent_customer_id?: string | null;
  bill_to_parent?: boolean;
  invoice_grouping?: 'subscription' | 'consolidated';
  legal_entity_id?: string | null;
  external_reference?: string | null;
  metadata?: Record<string, string | null>;
}

const customerEnvelope = itemEnvelope(customerSchema);

export function useCreateCustomer() {
  const client = useQueryClient();
  return useIdempotentMutation({
    mutationFn: async (input: CustomerInput, idempotencyKey) =>
      (await apiRequest(customerEnvelope, '/customers', { method: 'POST', body: input, idempotencyKey }))
        .data,
    onSuccess: (customer) => applyWrite(client, customers, customer),
  });
}

/** PATCH with `If-Match` on the version the form was opened with (412 if someone else saved first). */
export function useUpdateCustomer(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ input, version }: { input: CustomerInput; version?: number }) =>
      (
        await apiRequest(customerEnvelope, `/customers/${id}`, {
          method: 'PATCH',
          body: input,
          ifMatch: version,
        })
      ).data,
    onSuccess: (customer) => applyWrite(client, customers, customer),
  });
}

export function useArchiveCustomer(id: string) {
  const client = useQueryClient();
  return useIdempotentMutation({
    mutationFn: async (_: void, idempotencyKey) =>
      (await apiRequest(customerEnvelope, `/customers/${id}/archive`, { method: 'POST', idempotencyKey }))
        .data,
    onSuccess: (customer) => applyWrite(client, customers, customer),
  });
}

/** A customer's ledger position per currency (BillerAPI `CustomerBalanceResult`). */
export const customerBalanceSchema = z.object({
  customer_id: zId,
  balances: z.array(
    z.object({
      currency: z.string(),
      receivable_minor: zMinor,
      credit_minor: zMinor,
      net_minor: zMinor,
    }),
  ),
});
export type CustomerBalance = z.infer<typeof customerBalanceSchema>;

export function useCustomerBalance(id: string | undefined, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [...customers.keys.detail(id ?? ''), 'balance'],
    queryFn: async ({ signal }) =>
      (await apiRequest(itemEnvelope(customerBalanceSchema), `/customers/${id ?? ''}/balance`, { signal }))
        .data,
    enabled: Boolean(id) && (options.enabled ?? true),
  });
}
